import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Helper function to wait for App Bridge initialization
const waitForAppBridge = async (maxWaitTime = 5000) => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitTime) {
    if (window.getSessionToken && typeof window.getSessionToken === 'function') {
      console.log('✅ App Bridge is ready');
      return true;
    }
    
    // Check if we have Shopify context to initialize App Bridge
    const hasShopifyContext = window.SHOPIFY_SHOP || 
                             sessionStorage.getItem('shop') ||
                             new URLSearchParams(window.location.search).get('shop');
    
    if (!hasShopifyContext) {
      console.warn('⚠️ No Shopify context available, cannot initialize App Bridge');
      return false;
    }
    
    console.log('⏳ Waiting for App Bridge initialization...');
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  console.warn('⚠️ App Bridge initialization timeout');
  return false;
};

// Simple request interceptor that adds session token (exact Geofy pattern)
api.interceptors.request.use(
  async (config) => {
    console.log('🔍 [AXIOS DEBUG] Request config:', {
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      method: config.method,
      headers: config.headers
    });
    
    try {
      // Wait for App Bridge to be ready before proceeding
      const appBridgeReady = await waitForAppBridge();
      
      if (appBridgeReady && window.getSessionToken && typeof window.getSessionToken === 'function') {
        const token = await window.getSessionToken();
        
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          console.log('✅ Token added to request:', config.url);
        } else {
          console.warn('⚠️ No token received from App Bridge');
        }
      } else {
        console.warn('⚠️ getSessionToken not available, proceeding without token', {
          windowGetSessionToken: !!window.getSessionToken,
          typeOfGetSessionToken: typeof window.getSessionToken,
          windowAppBridge: !!window.appBridge,
          windowShopifyApiKey: !!window.SHOPIFY_API_KEY,
          windowShopifyHost: !!window.SHOPIFY_HOST,
          currentUrl: window.location.href,
          hasUrlParams: !!new URLSearchParams(window.location.search).get('host')
        });
      }
    } catch (error) {
      console.error('❌ Error getting session token:', error);
      // Continue without token - let the backend handle the authentication error
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with anti-loop protection
api.interceptors.response.use(
  (response) => {
    // Clear reload counter on successful requests
    sessionStorage.removeItem('reload_count');
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      console.warn('🔐 Authentication failed:', error.response?.data);
      
      // Check if this is a session-related error
      const isSessionError = error.response?.data?.error === 'Session not found';
      const errorDetails = error.response?.data?.details;
      
      // Anti-loop protection with reload counter
      const reloadCount = parseInt(sessionStorage.getItem('reload_count') || '0');
      const hasShopifyContext = window.SHOPIFY_SHOP || 
                               sessionStorage.getItem('shop') ||
                               new URLSearchParams(window.location.search).get('shop');
      
      // Don't reload for session errors - these need proper OAuth flow
      if (isSessionError) {
        console.warn('📋 Session needs to be created - temporary session will be used');
        sessionStorage.setItem('reload_count', '3'); // Prevent further reloads
        // Don't trigger reload for session errors
      } else if (hasShopifyContext && reloadCount < 3 && window.location.pathname !== '/auth/callback') {
        console.warn(`🔄 Reloading page to re-authenticate (attempt ${reloadCount + 1}/3)`);
        sessionStorage.setItem('reload_count', (reloadCount + 1).toString());
        window.location.reload();
      } else {
        console.warn('⚠️ Max reloads reached or no Shopify context - not reloading to prevent loop');
        sessionStorage.removeItem('reload_count');
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;